import type { Garment, Image } from '@/api/models';
import { GarmentSideSvg, GarmentSvg } from './GarmentSvg';

/**
 * Shows a product's photo when WooCommerce has one, and the illustrated
 * garment otherwise. Both render at the same size, so layouts don't change.
 */

interface Visual {
  garment: Garment;
  images?: { front: Image | null; back: Image | null; side?: Image | null } | null;
}

interface Props extends Visual {
  face?: 'front' | 'back';
  hanger?: boolean;
  /** Set when a surrounding element already names the product, so the image is skipped by screen readers. */
  decorative?: boolean;
  eager?: boolean;
  sizes?: string;
}

function Photo({ img, decorative, eager, sizes, className = 'garment-photo' }: { img: Image; decorative?: boolean; eager?: boolean; sizes?: string; className?: string }) {
  return (
    <img
      className={className}
      src={img.src}
      srcSet={img.srcSet}
      sizes={sizes ?? img.sizes}
      alt={decorative ? '' : img.alt}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      draggable={false}
    />
  );
}

export function GarmentVisual({ garment, images, face = 'front', hanger = true, decorative, eager, sizes }: Props) {
  // No dedicated back photo: show the front photo rather than mixing photo and illustration.
  const img = face === 'back' ? images?.back ?? images?.front : images?.front;
  if (img) return <Photo img={img} decorative={decorative} eager={eager} sizes={sizes} />;
  return <GarmentSvg garment={garment} face={face} hanger={hanger} />;
}

/**
 * The edge-on view for the rail, in order of preference: a side photo; the drawn edge-on
 * view in the product's colour (set in Rack display); a narrowed front photo.
 */
export function GarmentSide({ garment, images, drawn = true }: Visual & { drawn?: boolean }) {
  if (images?.side) return <Photo img={images.side} decorative sizes="40px" />;
  if (images?.front && !drawn) {
    return (
      <span className="side-derived">
        <Photo img={images.front} decorative sizes="180px" />
      </span>
    );
  }
  return <GarmentSideSvg garment={garment} />;
}
